import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule, DatePipe } from '@angular/common';
import { MaterialModule } from 'src/app/material.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TimeFormatPipe } from 'src/app/pipe/time.pipe';
import { TablerIconsModule } from 'angular-tabler-icons';
import { HttpClient } from '@angular/common/http';
import { OPDService } from '../../opd/opd.service';
import { UserService } from '../../user/user.service';
import * as constants from 'src/app/common/constant/constants.type'
import { PatientService } from '../../patient/patient.service';
import { GenderList } from 'src/app/common/types/common';
import { catchError, forkJoin, of, takeUntil, Subject } from 'rxjs';
import { Appointment } from '../../appointment/iappointment';
import { IPatientReport } from './ipatient-report';
import { IHospitalInfo } from '../../hospital/ihospital-info';
import { IPatientInfo } from '../../patient/ipatient-info';
import { IUsers } from '../../user/iusers';
import { CategoryType, CategoryTypeLabels } from 'src/app/common/enums/category-type';
import { Iopd } from '../../opd/opddetails/iopddetails';
import { IPrescription } from '../../opd/prescription/iprescription';
import { PrescriptionService } from '../../opd/prescription/prescription.service';
import { DrugService } from '../../settings/drug-tab/drug-list/drug.service';
import { IDrugDetail } from '../../settings/drug-tab/drug-list/idrug';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { environment } from 'src/environments/environment';
import { FormsModule } from '@angular/forms';
import { PrintSettingService } from '../../settings/hospital-setting/print-setting/print-setting.service';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';
import { EmptyStateComponent } from 'src/app/shared/empty-state/empty-state.component';

@Component({
  selector: 'app-report-print',
  imports: [CommonModule, MaterialModule, TranslateModule, TablerIconsModule, FormsModule, EmptyStateComponent],
  providers: [TimeFormatPipe, DatePipe,DefaultValuePipe],
  templateUrl: './patient-report-print.component.html',
  styleUrl: './patient-report-print.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PatientReportPrintComponent implements OnInit, OnDestroy {
  showIPDInfo: boolean = false;
  reportData: Appointment;
  opdData: Iopd | null = null;
  hospitalInfo: IHospitalInfo;
  doctorInfo: IUsers;
  patientDetails: IPatientInfo;
  PatientReport: IPatientReport[] = [];
  Prescriptions: IPrescription[] = [];
  drugList: IDrugDetail[] = [];
  drugMap: Map<number, string> = new Map();
  groupedReports: {
    categoryType: string;
    categories: {
      category: string;
      subCategories: {
        subCategory: string;
        items: any[];
      }[];
    }[];
  }[] = [];
  isDialog: boolean = false;
  isLoading: boolean = true;
  private pendingLoads: number = 0;
  @Input() appointmentData: Appointment;
  @Input() OpdData: Iopd | null = null;
  @Input() hideActionButtons: boolean = false;
  @Output() dataLoaded = new EventEmitter<void>();
  printTitle: string;
  @ViewChild('printbody') printSection!: ElementRef;
  @ViewChild('reportSection') reportSection!: ElementRef;
  @ViewChild('prescriptionSection') prescriptionSection!: ElementRef;
  CategoryTypeLabels = CategoryTypeLabels;
  displayOrder: CategoryType[] = [
    CategoryType.CaseHistory,
    CategoryType.ClinicalExam,
    CategoryType.SuggestedInvestigation,
    CategoryType.Diagnosis,
  ];
  staticimagepath = environment.imagePath;
  isPrescriptionPrint = false;
  @Input() showCategory: boolean = true;
  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private _opdService: OPDService,
    private patientService: PatientService,
    private datePipe: DatePipe,
    @Inject(MAT_DIALOG_DATA) @Optional() private _data: { appointmentDetails: Appointment, isDialog: true } | null,
    private userService: UserService,
    @Optional() private dialogRef: MatDialogRef<PatientReportPrintComponent>,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private _prescriptionService: PrescriptionService,
    private _drugService: DrugService,
    private hospitalState: HospitalStateService,
    private printSettingService: PrintSettingService,
    private defaultValuePipe: DefaultValuePipe
  ) {
    this.reportData = this._data?.appointmentDetails ?? this.appointmentData;
  }

  ngOnInit(): void {
    this.isDialog = this._data?.isDialog ?? false;

    if (!this._data && this.appointmentData) {
      this.reportData = { ...this.appointmentData };
    }
    if (this.OpdData) {
      this.reportData.patientName = this.OpdData.patientName;
      this.reportData.opdNumber = this.OpdData.opdNumber;
    }
    this.printTitle = this.translate.instant('ENTITY.PATIENT') + this.translate.instant('LABEL.REPORT');
    const doctorId = this.reportData.doctorId;
    const patientId = this.reportData.patientId;
    this.hospitalInfo = this.hospitalState.getHospital();

    forkJoin({
      doctor: this.userService.getUserByID(Number(doctorId)),
      patient: this.patientService.getPatientByID(Number(patientId))
    }).subscribe(({ doctor, patient }) => {
      this.doctorInfo = doctor.result;
      this.patientDetails = patient.result;
      this.cdr.markForCheck();
    });
    const opdId = this.OpdData?.id ?? this.reportData?.opdId;
    if (opdId) {
      forkJoin({
        opd: this._opdService.getPatientReport(Number(opdId)),
        prescriptions: this._prescriptionService.getPrescriptionDetailByOpdId(Number(opdId)),
        drugs: this._drugService.getDrugDetails().pipe(
          catchError((error) => {
            console.error('Error loading drug details in PatientReportPrintComponent:', error);
            return of({ result: [] as IDrugDetail[] });
          })
        )
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ({ opd, prescriptions, drugs }) => {
            this.PatientReport = opd.result ?? [];
            this.Prescriptions = Array.isArray(prescriptions.result)
              ? prescriptions.result
              : prescriptions.result ? [prescriptions.result] : [];

            this.Prescriptions.forEach(p => {
              if (!Array.isArray(p.prescriptionItems)) {
                p.prescriptionItems = [];
              }
            });

            this.drugList = drugs.result ?? [];
            this.drugMap = new Map(this.drugList.map(drug => [drug.id, drug.name]));

            this.groupReports();
            this.isLoading = false;
            this.dataLoaded.emit();
            this.cdr.markForCheck();
          },
          error: () => {
            this.isLoading = false;
            this.dataLoaded.emit();
            this.cdr.markForCheck();
          }
        });
    } else {
      this.loadDrugs();
      this.isLoading = false;
      this.cdr.markForCheck();
    }

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onLoadComplete(): void {
    this.pendingLoads--;
    if (this.pendingLoads <= 0) {
      this.isLoading = false;
      this.dataLoaded.emit();
    }
  }

  async printReportOnly() {
    const content = await this.createPrintContent(this.reportSection);
    this.openPrintWindow(content);
  }

  async printPrescriptionOnly() {
    this.isPrescriptionPrint = true;
    const content = await this.createPrintContent(this.prescriptionSection);
    this.openPrintWindow(content);
    this.isPrescriptionPrint = false;
  }

  async printReportDirect() {
    const printContent = await this.createPrintContent(this.printSection);
    this.openPrintWindow(printContent);
  }

  private async createPrintContent(sectionRef: ElementRef): Promise<string> {
    try {
      // 1️⃣ Load the HTML template from assets
      let template = await this.http
        .get('assets/print-template.html', { responseType: 'text' })
        .toPromise();

      if (!template) throw new Error('Template not found');

      if (!this.showIPDInfo) {
        template = template.replace(
          '</head>',
          `<style>
        #showIPDInfo { display: none !important; }
      </style></head>`
        );
      }

      // 2️⃣ Prepare all dynamic values
      const opdDate = this.datePipe.transform(
        this.reportData?.appointDate,
        constants.DDMMYYYY_FORMAT
      );

      const patientAgeGender = `${this.getGenderName(
        this.patientDetails?.gender
      )} / ${this.patientDetails?.age}`;

      const patientName = `${this.patientDetails.firstName} ${this.patientDetails.lastName}`

      const doctorName = `${this.doctorInfo?.firstName ?? ''} ${this.doctorInfo?.lastName ?? ''}`.trim();

      const reportHtml = sectionRef.nativeElement.innerHTML;
      const fullImagePath = this.hospitalInfo?.logo ? `${this.staticimagepath}${this.hospitalInfo.logo}` : null;
      const defaultLogo = `${window.location.origin}/assets/images/svgs/hospital.png`;
      const settings = await this.printSettingService.getPrintSettingPromise(this.hospitalInfo.id);

      // 3️⃣ Create a key-value map of all placeholders
      const replacements: Record<string, string> = {
        reportTitle: `${this.printTitle}-${this.reportData.opdNumber ?? ''}`,
        hospitalName: this.defaultValuePipe.transform(this.hospitalInfo?.name, ''),
        hospitalAddress: this.defaultValuePipe.transform(this.hospitalInfo?.address, ''),
        phone: this.defaultValuePipe.transform(this.hospitalInfo?.phone, ''),
        regNo: this.defaultValuePipe.transform(this.hospitalInfo?.registrationNumber, ''),
        patientName: patientName ?? '',
        patientAge: patientAgeGender,
        opdNumber: this.reportData.opdNumber ?? '',
        opdDate: opdDate ?? '',
        doctorName: doctorName ?? '',
        qualification: this.defaultValuePipe.transform(this.doctorInfo?.degree, ''),
        doctorRegistrationNo: this.doctorInfo?.registrationNumber 
          ? `Reg No. ${this.defaultValuePipe.transform(this.doctorInfo?.registrationNumber, '')}` 
          : '',
        doctorPhone: this.doctorInfo?.phoneNumber 
          ? `M. ${this.defaultValuePipe.transform(this.doctorInfo?.phoneNumber, '')}` 
          : '',
        reportSections: reportHtml ?? '',
        logo: `<img src="${fullImagePath || defaultLogo}" class="logo" onerror="this.onerror=null;this.src='${defaultLogo}'"/>`
      };

      // 4️⃣ Replace placeholders dynamically using regex
      let filledTemplate = template;
      for (const key in replacements) {
        const value = replacements[key] ?? '';
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
        filledTemplate = filledTemplate.replace(regex, value);
      }

      // Apply settings
      let styles = '';
      if (settings) {
        const logoPos = settings.logoPosition === 'center' ? 'column' : (settings.logoPosition === 'left' ? 'row' : 'row-reverse');
        const headerClass = settings.logoPosition === 'center' ? 'center' : '';
        const headerAlignment = settings.addressAlignment || 'left';

        styles = `
            :root {
                --logo-size: ${settings.logoSize || '60px'};
                --logo-position: ${logoPos};
                --header-alignment: ${headerAlignment};
                --hospital-name-size: ${settings.hospitalNameSize || 20}px;
                --address-font-size: ${settings.addressFontSize || 13}px;
                --footer-display: ${settings.showFooter ? 'block' : 'none'};
                --doctor-signature-top-display: ${settings.showFooter && settings.doctorNamePosition === 'top' ? 'block' : 'none'};
                --doctor-signature-bottom-display: ${settings.showFooter && settings.doctorNamePosition === 'footer' ? 'block' : 'none'};
            }
            #patientInfo { display: block !important; }
        `;
        filledTemplate = filledTemplate.replace('</head>', `<style>${styles}</style></head>`).replace('{{headerClass}}', headerClass);
      } else {
        styles = '#patientInfo { display: block !important; }';
        filledTemplate = filledTemplate.replace('</head>', `<style>${styles}</style></head>`).replace('{{headerClass}}', '');
      }

      return filledTemplate;

    } catch (error) {
      console.error('Error generating print content:', error);
      return '<h3>Unable to generate report.</h3>';
    }
  }

  private openPrintWindow(content: string): void {

    let iframe = document.getElementById('print-frame') as HTMLIFrameElement;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'print-frame';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow?.document;

    if (!doc) {
      return;
    }

    doc.open();
    doc.write(content);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.print();
    }, 300);
  }

  getOpdData(opdId: string): void {
    this._opdService.getPatientReport(Number(opdId)).subscribe({
      next: (res) => {
        this.PatientReport = res.result;
        this.groupReports();
        this.onLoadComplete();
      },
      error: () => {
        this.onLoadComplete();
      }
    });
  }

  getPrescriptions(opdId: string): void {
    this._prescriptionService.getPrescriptionDetailByOpdId(Number(opdId)).subscribe({
      next: (res) => {
        const result = res.result;
        this.Prescriptions = Array.isArray(result) ? result : result ? [result] : [];
        this.Prescriptions.forEach(p => {
          if (!Array.isArray(p.prescriptionItems)) p.prescriptionItems = [];
        });
        this.onLoadComplete();
      },
      error: () => {
        this.onLoadComplete();
      }
    });
  }

  get diagnosisList(): string {
    if (!this.Prescriptions || this.Prescriptions.length === 0) {
      return '';
    }
    return this.Prescriptions
      .map(p => p.diagnosis)
      .filter(d => d)
      .join(', ');
  }

  getDrugName(drugId: number): string {
    return this.drugMap.get(drugId) ?? '';
  }

  loadDrugs() {
    this._drugService.getDrugDetails().subscribe(resp => {
      this.drugList = resp.result ?? [];
      this.drugMap = new Map(this.drugList.map(drug => [drug.id, drug.name]));
      this.cdr.markForCheck();
    });
  }

  groupReports() {
    const categoryTypeMap = new Map<string, Map<string, Map<string, any[]>>>();

    for (let report of this.PatientReport) {
      const categoryType = report.categoryTypeName;
      const category = report.categoryName;
      const subCategory = report.subCategoryName;
      // Ensure categoryType exists
      if (!categoryTypeMap.has(categoryType)) {
        categoryTypeMap.set(categoryType, new Map<string, Map<string, any[]>>());
      }

      const categoryMap = categoryTypeMap.get(categoryType)!;
      // Ensure category exists
      if (!categoryMap.has(category)) {
        categoryMap.set(category, new Map<string, any[]>());
      }

      const subCategoryMap = categoryMap.get(category)!;

      // Ensure subCategory exists
      if (!subCategoryMap.has(subCategory)) {
        subCategoryMap.set(subCategory, []);
      }

      // Push report into subCategory
      subCategoryMap.get(subCategory)!.push(report);
    }

    const normalize = (value: string) => value.replace(/\s+/g, '').toLowerCase();

    const getOrderIndex = (categoryTypeName: string): number => {
      const match = Object.entries(CategoryTypeLabels).find(
        ([, label]) => normalize(String(label)) === normalize(categoryTypeName)
      );
      const enumValue = match ? Number(match[0]) : null;
      const index = this.displayOrder.indexOf(enumValue as CategoryType);
      return index === -1 ? 999 : index; // push unknown categories to the end
    };

    // Convert to array structure for *ngFor
    this.groupedReports = Array.from(categoryTypeMap, ([categoryType, categoryMap]) => ({
      categoryType,
      categories: Array.from(categoryMap, ([category, subCategoryMap]) => ({
        category,
        subCategories: Array.from(subCategoryMap, ([subCategory, items]) => ({ subCategory, items }))
      }))
    })).sort((a, b) => getOrderIndex(a.categoryType) - getOrderIndex(b.categoryType));
  }

  getGenderName(value?: string): string {
    const gender = GenderList.find(g => g.value === value);
    return gender ? gender.name : '';
  }

  onCloseDialog() {
    this.dialogRef.close();
  }

  get noDataAvailable(): boolean {
    const hasReports = this.groupedReports?.length > 0;
    const hasPrescriptions = this.Prescriptions?.length > 0;
    return !hasReports && !hasPrescriptions;
  }
}